import * as THREE from 'three';
import { Input } from '../../core/Input';
import { Camera, CameraMode } from '../../rendering/Camera';
import { Scene } from '../../scene/Scene';
import { BlenderTower } from '../../objects/dynamic/BlenderTower';
import { ObjectManager } from '../../objects/ObjectManager';
import { GameObject, ObjectType } from '../../objects/GameObject';
import { StaticObjectType } from '../../objects/static/LODStaticObject';
import { LODLoaf } from '../../objects/static/LODLoaf';
import { ObjectFactory } from '../../objects/ObjectFactory';
import { Placeable } from '../../objects/Placeable';
import { BoundingVolume, BoundingVolumeType, BoundingSphere } from '../../physics/BoundingVolume';
import { GameState } from '../../game/GameState';
import { Config } from '../../core/Config';
import { ProjectileSystem } from '../../projectiles/ProjectileSystem';
import { ProjectileType } from '../../projectiles/AbstractProjectile';
// Import nipplejs for mobile joystick
import nipplejs from 'nipplejs';

/**
 * Enum for weapon modes in the game
 */
export enum WeaponMode {
  TOWER_PLACEMENT,
  GUN,
  AUTOMATIC_GUN
}

export class WeaponSystem {
  private input: Input;
  private scene: THREE.Scene;
  private camera: Camera;
  private currentMode: WeaponMode = WeaponMode.TOWER_PLACEMENT;
  private weaponUI: HTMLDivElement = document.createElement('div');
  private towerButton: HTMLButtonElement = document.createElement('button');
  private gunButton: HTMLButtonElement = document.createElement('button');
  private autoGunButton: HTMLButtonElement = document.createElement('button');
  private crosshair: HTMLDivElement = document.createElement('div');
  
  // Tower placement properties
  private objectManager: ObjectManager | null = null;
  private objectFactory: ObjectFactory | null = null;
  private towerPlacementIndicator: THREE.Mesh | null = null;
  private towerActionRadius: number = 15; // Same as BlenderTower targeting radius
  private canPlaceTower: boolean = true;
  private towerIndicatorMaterial: THREE.MeshBasicMaterial | null = null;
  private threeJsScene: THREE.Scene | null = null;
  private terrainMesh: THREE.Mesh | null = null;
  private raycaster: THREE.Raycaster = new THREE.Raycaster();
  private mouse: THREE.Vector2 = new THREE.Vector2();
  private maxPlacementDistance: number = 15; // Maximum distance for tower placement
  private wasMouseLocked: boolean = false; // Track if mouse was just locked
  
  // Game state for economy
  private gameState: GameState;
  
  // Projectile system for weapons
  private projectileSystem: ProjectileSystem | null = null;
  
  // Gun model properties
  private gunModel: THREE.Group | null = null;
  private gunBarrelTip: THREE.Vector3 = new THREE.Vector3();
  private gunOffset: THREE.Vector3 = new THREE.Vector3(0.5, -0.3, -0.8); // Position relative to camera
  
  // Automatic gun properties
  private isAutoFiring: boolean = false;
  private lastAutoFireTime: number = 0;
  private autoFireRate: number = 0.1; // Seconds between shots (10 shots per second)
  private autoGunSpread: number = 0.03; // Random spread for bullets
  private autoGunCostPerShot: number = 1; // Cost per shot is low for auto gun
  
  // Mobile control properties
  private leftJoystick: any = null;
  private rightJoystick: any = null;
  private leftActionButton: HTMLDivElement | null = null;
  private rightActionButton: HTMLDivElement | null = null;
  private runButton: HTMLDivElement | null = null;
  private isRunActive: boolean = false;
  private isMobileControlsActive: boolean = false;
  private mobileMovementVector: THREE.Vector2 = new THREE.Vector2();
  private mobileLookVector: THREE.Vector2 = new THREE.Vector2();
  
  constructor(camera: Camera, scene: Scene | THREE.Scene, input: Input) {
    this.camera = camera;
    this.input = input;
    this.gameState = GameState.getInstance();
    
    // Store the THREE scene reference
    if (scene instanceof Scene) {
      this.scene = scene.getThreeScene();
      this.threeJsScene = scene.getThreeScene();
      this.terrainMesh = scene.getTerrain().getMesh();
      this.objectManager = scene.getObjectManager();
      this.objectFactory = scene.getObjectFactory();
      
      // Try to get the ProjectileSystem from the scene
      // We'll need to inject it separately since Scene doesn't expose this method
      this.projectileSystem = null; // Will be set via setProjectileSystem later
    } else {
      // Assume it's a THREE.Scene
      this.scene = scene;
      this.threeJsScene = scene;
      
      // Try to find terrain mesh in scene
      scene.traverse((object) => {
        if (object instanceof THREE.Mesh && 
            object.geometry instanceof THREE.PlaneGeometry &&
            !this.terrainMesh) {
          this.terrainMesh = object;
        }
      });
      
      // Object manager will need to be set separately
      console.warn('ObjectManager not available - tower placement will not work until setObjectManager is called');
    }
    
    if (!this.terrainMesh) {
      console.error('Could not find terrain mesh for tower placement');
    }
    
    // Create crosshair
    this.createCrosshair();
    
    // Create weapon mode UI
    this.createWeaponUI();
    
    // Create tower placement indicator
    this.createTowerPlacementIndicator();
    
    // Create gun model
    this.createGunModel();
    
    // Set initial mode
    this.setWeaponMode(WeaponMode.TOWER_PLACEMENT);
    
    // Add a direct keyboard event listener for weapon switching
    document.addEventListener('keydown', (event) => {
      // Only handle keys in first person mode
      if (this.camera.getCameraMode() !== CameraMode.FIRST_PERSON) return;
      
      // Handle weapon switching with number keys
      if (event.key === '1') {
        this.setWeaponMode(WeaponMode.TOWER_PLACEMENT);
      } else if (event.key === '2') {
        this.setWeaponMode(WeaponMode.GUN);
      }
    });
    
    // Track pointer lock change to prevent tower placement when locking cursor
    document.addEventListener('pointerlockchange', () => {
      // Set wasMouseLocked to true when pointer is locked, to prevent immediate tower placement
      this.wasMouseLocked = !!document.pointerLockElement;
    });
    
    // Add alternative click handling - direct DOM event for more reliable detection
    window.addEventListener('mousedown', (event) => {
      // Skip if it's a right-click or non-left click
      if (event.button !== 0) return;
      
      // Skip if clicking on UI elements
      if (event.target instanceof HTMLElement) {
        const isUIElement = event.target.id === 'weapon-ui' || 
                          event.target.tagName === 'BUTTON' || 
                          event.target.closest('#weapon-ui');
        if (isUIElement) {
          return;
        }
      }
      
      const isFirstPerson = this.camera.getCameraMode() === CameraMode.FIRST_PERSON;
      
      // If the mouse was just locked, don't place a tower on this click
      if (this.wasMouseLocked) {
        this.wasMouseLocked = false;
        return;
      }
      
      if (this.currentMode === WeaponMode.TOWER_PLACEMENT && 
          isFirstPerson &&
          this.canPlaceTower) {
        this.placeTower();
      } else if (this.currentMode === WeaponMode.GUN && isFirstPerson) {
        this.fireGun();
      } else if (this.currentMode === WeaponMode.AUTOMATIC_GUN && isFirstPerson) {
        this.isAutoFiring = true;
      }
    });
    
    // Handle mouseup to stop auto-firing
    window.addEventListener('mouseup', (event) => {
      if (event.button === 0 && this.currentMode === WeaponMode.AUTOMATIC_GUN) {
        this.isAutoFiring = false;
      }
    });
  }
  
  private createCrosshair(): void {
    // Create a simple crosshair
    this.crosshair.id = 'crosshair';
    this.crosshair.style.position = 'absolute';
    this.crosshair.style.top = '50%';
    this.crosshair.style.left = '50%';
    this.crosshair.style.transform = 'translate(-50%, -50%)';
    this.crosshair.style.width = '20px';
    this.crosshair.style.height = '20px';
    this.crosshair.style.pointerEvents = 'none'; // Make sure it doesn't interfere with clicks
    
    // Create crosshair lines
    const horizontalLine = document.createElement('div');
    horizontalLine.style.position = 'absolute';
    horizontalLine.style.top = '50%';
    horizontalLine.style.left = '0';
    horizontalLine.style.width = '100%';
    horizontalLine.style.height = '2px';
    horizontalLine.style.backgroundColor = 'white';
    horizontalLine.style.transform = 'translateY(-50%)';
    
    const verticalLine = document.createElement('div');
    verticalLine.style.position = 'absolute';
    verticalLine.style.top = '0';
    verticalLine.style.left = '50%';
    verticalLine.style.width = '2px';
    verticalLine.style.height = '100%';
    verticalLine.style.backgroundColor = 'white';
    verticalLine.style.transform = 'translateX(-50%)';
    
    // Add lines to crosshair
    this.crosshair.appendChild(horizontalLine);
    this.crosshair.appendChild(verticalLine);
    
    // Add crosshair to document
    document.body.appendChild(this.crosshair);
  }
  
  private createWeaponUI(): void {
    // Create weapon mode UI container
    this.weaponUI.id = 'weapon-ui';
    this.weaponUI.style.position = 'absolute';
    this.weaponUI.style.bottom = '20px';
    this.weaponUI.style.left = '50%';
    this.weaponUI.style.transform = 'translateX(-50%)';
    this.weaponUI.style.display = 'flex';
    this.weaponUI.style.gap = '10px';
    this.weaponUI.style.padding = '5px';
    this.weaponUI.style.borderRadius = '5px';
    this.weaponUI.style.backgroundColor = 'rgba(0, 0, 0, 0.5)';
    
    // Create Tower button
    const towerCost = this.gameState.getTowerCost();
    this.towerButton.textContent = `Place Tower [💰${towerCost}]`;
    this.towerButton.style.padding = '8px 15px';
    this.towerButton.style.border = 'none';
    this.towerButton.style.borderRadius = '3px';
    this.towerButton.style.backgroundColor = 'rgba(0, 100, 200, 0.8)';
    this.towerButton.style.color = 'white';
    this.towerButton.style.fontFamily = 'Arial, sans-serif';
    this.towerButton.style.fontSize = '14px';
    this.towerButton.style.cursor = 'pointer';
    this.towerButton.style.transition = 'background-color 0.2s';
    this.towerButton.onclick = () => this.setWeaponMode(WeaponMode.TOWER_PLACEMENT);
    
    // Create Gun button
    const gunCost = this.gameState.getGunCost();
    this.gunButton.textContent = `Use Gun [💰${gunCost}/shot]`;
    this.gunButton.style.padding = '8px 15px';
    this.gunButton.style.border = 'none';
    this.gunButton.style.borderRadius = '3px';
    this.gunButton.style.backgroundColor = 'rgba(80, 80, 80, 0.8)';
    this.gunButton.style.color = 'white';
    this.gunButton.style.fontFamily = 'Arial, sans-serif';
    this.gunButton.style.fontSize = '14px';
    this.gunButton.style.cursor = 'pointer';
    this.gunButton.style.transition = 'background-color 0.2s';
    this.gunButton.onclick = () => this.setWeaponMode(WeaponMode.GUN);
    
    // Create Auto Gun button
    this.autoGunButton.textContent = `Auto Gun [💰${this.autoGunCostPerShot}/shot]`;
    this.autoGunButton.style.padding = '8px 15px';
    this.autoGunButton.style.border = 'none';
    this.autoGunButton.style.borderRadius = '3px';
    this.autoGunButton.style.backgroundColor = 'rgba(0, 150, 100, 0.8)';
    this.autoGunButton.style.color = 'white';
    this.autoGunButton.style.fontFamily = 'Arial, sans-serif';
    this.autoGunButton.style.fontSize = '14px';
    this.autoGunButton.style.cursor = 'pointer';
    this.autoGunButton.style.transition = 'background-color 0.2s';
    this.autoGunButton.onclick = () => this.setWeaponMode(WeaponMode.AUTOMATIC_GUN);
    
    // Add buttons to UI
    this.weaponUI.appendChild(this.towerButton);
    this.weaponUI.appendChild(this.gunButton);
    
    // Add UI to document
    document.body.appendChild(this.weaponUI);
  }
  
  private createTowerPlacementIndicator(): void {
    if (!this.threeJsScene) return;
    
    // Get the correct placement size from the BlenderTower implementation
    const placementSize = 4.5; // Default size as fallback
    
    // Create a circular indicator for tower placement
    const geometry = new THREE.CircleGeometry(placementSize, 32);
    this.towerIndicatorMaterial = new THREE.MeshBasicMaterial({
      color: 0x66ff66, // Brighter green color for better visibility
      transparent: true,
      opacity: 0.5,
      side: THREE.DoubleSide,
      depthTest: false, // Prevents z-fighting with terrain
      depthWrite: false // Ensures it's always rendered on top
    });
    
    this.towerPlacementIndicator = new THREE.Mesh(geometry, this.towerIndicatorMaterial);
    
    // Rotate to be parallel to the ground
    this.towerPlacementIndicator.rotation.x = -Math.PI / 2;
    
    // Set position slightly above ground to avoid z-fighting
    this.towerPlacementIndicator.position.y = 0.1;
    
    // Initially hide the indicator
    this.towerPlacementIndicator.visible = false;
    
    // Add to scene
    this.threeJsScene.add(this.towerPlacementIndicator);
  }
  
  /**
   * Create a gun model for the first-person view
   */
  private createGunModel(): void {
    // Skip if scene not available
    if (!this.scene) return;
    
    this.gunModel = new THREE.Group();
    
    // Create gun body using simple shapes
    const gunBodyMaterial = new THREE.MeshStandardMaterial({
      color: 0x444444,
      metalness: 0.7,
      roughness: 0.2
    });
    
    // Main gun body
    const gunBody = new THREE.Mesh(
      new THREE.BoxGeometry(0.1, 0.15, 0.4),
      gunBodyMaterial
    );
    this.gunModel.add(gunBody);
    
    // Gun barrel
    const gunBarrel = new THREE.Mesh(
      new THREE.CylinderGeometry(0.03, 0.03, 0.6, 8),
      gunBodyMaterial
    );
    gunBarrel.rotation.x = Math.PI / 2; // Rotate to point forward
    gunBarrel.position.z = -0.5; // Position in front of body
    gunBarrel.position.y = 0.05; // Align with top of gun body
    this.gunModel.add(gunBarrel);
    
    // Gun handle
    const gunHandle = new THREE.Mesh(
      new THREE.BoxGeometry(0.07, 0.2, 0.1),
      gunBodyMaterial
    );
    gunHandle.position.y = -0.15; // Position below gun body
    gunHandle.position.z = 0.05; // Slightly back from center
    this.gunModel.add(gunHandle);
    
    // Create a point for the barrel tip (where projectiles spawn)
    const barrelTip = new THREE.Object3D();
    barrelTip.position.set(0, 0.05, -0.8); // Position at end of barrel
    this.gunModel.add(barrelTip);
    
    // Store barrel tip position for projectile spawning
    this.gunBarrelTip.copy(barrelTip.position);
    
    // Don't add to scene yet - add/remove based on selected weapon
  }
  
  public update(deltaTime: number): void {
    // Check for weapon mode switches
    if (this.input.isKeyPressed('1')) {
      this.setWeaponMode(WeaponMode.TOWER_PLACEMENT);
    } else if (this.input.isKeyPressed('2')) {
      this.setWeaponMode(WeaponMode.GUN);
    }
    
    // Update UI visibility based on camera mode
    const isFirstPerson = this.camera.getCameraMode() === CameraMode.FIRST_PERSON;
    this.weaponUI.style.display = isFirstPerson ? 'flex' : 'none';
    this.crosshair.style.display = isFirstPerson ? 'block' : 'none';
    
    // Show/hide mobile controls based on camera mode
    if (this.isMobileControlsActive) {
      const joystickDisplay = isFirstPerson ? 'block' : 'none';
      
      if (document.getElementById('left-joystick-container')) {
        document.getElementById('left-joystick-container')!.style.display = joystickDisplay;
      }
      
      if (document.getElementById('right-joystick-container')) {
        document.getElementById('right-joystick-container')!.style.display = joystickDisplay;
      }
      
      if (this.leftActionButton) {
        this.leftActionButton.style.display = joystickDisplay;
      }
      
      if (this.rightActionButton) {
        this.rightActionButton.style.display = joystickDisplay;
      }
      
      if (this.runButton) {
        this.runButton.style.display = joystickDisplay;
      }
      
      // Apply mobile movement to camera
      if (isFirstPerson && this.mobileMovementVector.length() > 0) {
        // Apply run mode if active (simulate shift key)
        if (this.isRunActive) {
          this.input.simulateKeyHold('shift');
        } else {
          this.input.simulateKeyRelease('shift');
        }
        
        // Apply a larger deadzone to movement to prevent unintended movement
        const moveDeadzone = 0.2;
        
        // Forward/back movement (W/S keys)
        if (Math.abs(this.mobileMovementVector.y) > moveDeadzone) {
          // Important: In nipplejs, positive Y is up (forward), negative Y is down (backward)
          // This means we need to directly use the Y value to determine W vs S
          const direction = this.mobileMovementVector.y > 0 ? 'w' : 's';
          this.input.simulateKeyHold(direction);
          
          // Release the opposite direction key to prevent conflicting inputs
          this.input.simulateKeyRelease(direction === 'w' ? 's' : 'w');
          
          // Log the movement for debugging
          console.log(`Mobile movement: ${direction} (y=${this.mobileMovementVector.y.toFixed(2)})`);
        } else {
          // Release both forward/backward keys when in the deadzone
          this.input.simulateKeyRelease('w');
          this.input.simulateKeyRelease('s');
        }
        
        // Left/right movement (A/D keys)
        if (Math.abs(this.mobileMovementVector.x) > moveDeadzone) {
          // For left/right, negative X is left (A), positive X is right (D)
          const direction = this.mobileMovementVector.x < 0 ? 'a' : 'd';
          this.input.simulateKeyHold(direction);
          
          // Release the opposite direction key to prevent conflicting inputs
          this.input.simulateKeyRelease(direction === 'a' ? 'd' : 'a');
        } else {
          // Release both left/right keys when in the deadzone
          this.input.simulateKeyRelease('a');
          this.input.simulateKeyRelease('d');
        }
      }
      
      // Apply mobile look to camera
      if (isFirstPerson && this.mobileLookVector.length() > 0) {
        // Apply a small deadzone to avoid tiny, unintended movements
        const deadzone = 0.1;
        
        let xMovement = 0;
        let yMovement = 0;
        
        // Only use ONE movement axis at a time to prevent any tilting effect
        if (Math.abs(this.mobileLookVector.x) > deadzone) {
          // Map to a curve that's more sensitive in the middle of the joystick range
          const normalizedX = (Math.abs(this.mobileLookVector.x) - deadzone) / (1.0 - deadzone);
          const curvedX = Math.pow(normalizedX, 1.5); // Apply a gentle power curve
          xMovement = Math.sign(this.mobileLookVector.x) * curvedX * 1.2; // Slightly increase horizontal sensitivity
          
          // Ensure only horizontal movement is applied
          yMovement = 0;
        } 
        else if (Math.abs(this.mobileLookVector.y) > deadzone) {
          // Apply a lower sensitivity for vertical looking to avoid overshooting
          const normalizedY = (Math.abs(this.mobileLookVector.y) - deadzone) / (1.0 - deadzone);
          const curvedY = Math.pow(normalizedY, 1.8); // Steeper curve for vertical to give finer control
          yMovement = Math.sign(this.mobileLookVector.y) * curvedY * 0.8; // Reduce vertical sensitivity
          
          // Ensure only vertical movement is applied
          xMovement = 0;
        }
        
        // Apply the look movement (Y is inverted for standard FPS controls)
        if (xMovement !== 0 || yMovement !== 0) {
          this.camera.applyMouseLook(xMovement, -yMovement);
        }
      }
    }
    
    // Update tower placement indicator in first person mode
    if (isFirstPerson && this.currentMode === WeaponMode.TOWER_PLACEMENT) {
      this.updateTowerPlacementIndicator();
      // Hide gun model
      if (this.gunModel && this.gunModel.parent) {
        this.scene.remove(this.gunModel);
      }
    } else if (this.towerPlacementIndicator) {
      this.towerPlacementIndicator.visible = false;
    }
    
    // Update gun model in first person mode
    if (isFirstPerson && (this.currentMode === WeaponMode.GUN || this.currentMode === WeaponMode.AUTOMATIC_GUN)) {
      this.updateGunModel();
    }
    
    // Handle automatic firing
    if (isFirstPerson && this.currentMode === WeaponMode.AUTOMATIC_GUN && this.isAutoFiring) {
      const currentTime = performance.now() / 1000;
      if (currentTime - this.lastAutoFireTime >= this.autoFireRate) {
        this.fireAutoGun();
        this.lastAutoFireTime = currentTime;
      }
    }
    
    // Update action button texts based on current mode
    if (this.isMobileControlsActive) {
      this.updateMobileActionButtons();
    }
  }
  
  /**
   * Update the gun model position and rotation based on camera
   */
  private updateGunModel(): void {
    if (!this.gunModel || !this.scene) return;
    
    // Get camera information
    const camera = this.camera.getThreeCamera();
    
    // Make sure gun is added to scene
    if (!this.gunModel.parent) {
      this.scene.add(this.gunModel);
    }
    
    // Position the gun relative to the camera
    this.gunModel.position.copy(camera.position);
    
    // Apply offset relative to camera's view direction
    const offset = this.gunOffset.clone();
    
    // Convert the offset from camera's local space to world space
    const quaternion = camera.quaternion.clone();
    offset.applyQuaternion(quaternion);
    
    // Add the offset to the gun position
    this.gunModel.position.add(offset);
    
    // Match gun rotation to camera
    this.gunModel.quaternion.copy(camera.quaternion);
  }
  
  private updateTowerPlacementIndicator(): void {
    if (!this.towerPlacementIndicator || !this.terrainMesh || !this.threeJsScene) {
      return;
    }
    
    // Make sure indicator is in the scene
    if (!this.towerPlacementIndicator.parent) {
      this.threeJsScene.add(this.towerPlacementIndicator);
    }
    
    // Set ray from camera through crosshair (center of screen)
    const camera = this.camera.getThreeCamera();
    this.raycaster.setFromCamera(new THREE.Vector2(0, 0), camera);
    
    // Check for intersection with terrain specifically
    const intersects = this.raycaster.intersectObject(this.terrainMesh, false);
    
    // If no terrain intersection, try with all objects
    if (intersects.length === 0) {
      // Try with all objects in the scene as fallback
      const allIntersects = this.raycaster.intersectObjects(this.threeJsScene.children, true);
      
      // Find first ground-like intersection (near y=0)
      const groundIntersect = allIntersects.find(hit => Math.abs(hit.point.y) < 1);
      if (groundIntersect) {
        // Check if the intersection is within the maximum placement distance
        if (groundIntersect.distance <= this.maxPlacementDistance) {
          // Use this intersection point
          const hitPoint = groundIntersect.point;
          this.updateIndicatorPosition(hitPoint);
        } else {
          // Too far away, hide the indicator
          this.towerPlacementIndicator.visible = false;
          this.canPlaceTower = false;
        }
      } else {
        // Create a fixed distance point in front of the camera as last resort
        const cameraDir = new THREE.Vector3(0, 0, -1).applyQuaternion(camera.quaternion);
        const hitPoint = new THREE.Vector3()
          .copy(camera.position)
          .add(cameraDir.multiplyScalar(Math.min(20, this.maxPlacementDistance))); // Limit to max placement distance
        hitPoint.y = 0.1; // Just above ground level
        this.updateIndicatorPosition(hitPoint);
      }
    } else {
      // Check if the intersection is within the maximum placement distance
      if (intersects[0].distance <= this.maxPlacementDistance) {
        // Use the first terrain intersection
        const hitPoint = intersects[0].point;
        this.updateIndicatorPosition(hitPoint);
      } else {
        // Too far away, hide the indicator
        this.towerPlacementIndicator.visible = false;
        this.canPlaceTower = false;
      }
    }
  }
  
  // Helper method to update indicator position and appearance
  private updateIndicatorPosition(position: THREE.Vector3): void {
    if (!this.towerPlacementIndicator || !this.towerIndicatorMaterial) return;
    
    // Position the indicator at the hit point
    this.towerPlacementIndicator.position.copy(position);
    this.towerPlacementIndicator.position.y += 0.2; // Increased offset to prevent z-fighting
    
    // Show the indicator
    this.towerPlacementIndicator.visible = true;
    
    // Check if tower can be placed here
    this.canPlaceTower = this.checkTowerPlacement(position);
    
    // Update indicator color based on placement validity
    this.towerIndicatorMaterial.color.set(this.canPlaceTower ? 0x66ff66 : 0xff3333);
    // Make invalid placement more visible
    this.towerIndicatorMaterial.opacity = this.canPlaceTower ? 0.5 : 0.7;
  }
  
  private checkTowerPlacement(position: THREE.Vector3): boolean {
    if (!this.objectManager) return true;
    
    // Get the tower placement size from the implementation
    const towerPlacementSize = 4.5; // Default to our fixed value
    
    // Get objects within tower placement radius
    const objectsInRadius = this.objectManager.getObjectsInRadius(position, towerPlacementSize * 2);
    
    // Check for collision with other towers' placement areas
    for (const object of objectsInRadius) {
      // Check if the object is a BlenderTower
      if (object instanceof BlenderTower) {
        const towerDistance = position.distanceTo(object.getPosition());
        // If two towers placement areas overlap
        if (towerDistance < towerPlacementSize * 2) {
          return false;
        }
      }
      
      // Check if the object is a bread loaf (shouldn't build towers near it)
      if (object instanceof LODLoaf) {
        // Get the distance to the loaf
        const loafDistance = position.distanceTo(object.getPosition());
        
        // Get the loaf's bounding volume radius (or use a default if not available)
        let loafRadius = 2.0; // Default value
        const boundingVolume = object.getBoundingVolume();
        if (boundingVolume) {
          // If it's a sphere, we can get the radius directly from getSize()
          if (boundingVolume.getType() === BoundingVolumeType.SPHERE) {
            loafRadius = boundingVolume.getSize() as number;
          } else {
            // For other volume types, use the largest dimension as an approximation
            const size = boundingVolume.getSize() as THREE.Vector3;
            loafRadius = Math.max(size.x, size.y, size.z);
          }
        }
        
        // Check if tower placement radius touches the loaf's boundary
        if (loafDistance < (towerPlacementSize + loafRadius)) {
          return false; // Don't allow placement if too close to loaf
        }
      }
    }
    
    return true;
  }
  
  /**
   * Place a tower at the current position of the indicator
   */
  private placeTower(): void {
    const towerCost = this.gameState.getTowerCost();
    
    // Check if player has enough gold
    if (!this.gameState.hasEnoughGold(towerCost)) {
      console.log(`Cannot place tower: Not enough gold (need ${towerCost}, have ${this.gameState.getGold()})`);
      this.showNotEnoughGoldMessage();
      return;
    }
    
    if (!this.objectFactory || !this.towerPlacementIndicator) {
      console.error("Cannot place tower: Missing objectFactory or indicator");
      return;
    }
    
    // Get position from indicator
    const position = this.towerPlacementIndicator.position.clone();
    
    if (!this.canPlaceTower) {
      console.log('Cannot place tower here - invalid location');
      return;
    }
    
    // Create the tower
    console.log(`Placing tower at ${position.x}, ${position.y}, ${position.z}`);
    const tower = this.objectFactory.createBlenderTower(position.x, position.z);
    
    if (tower) {
      console.log('Tower placed successfully!');
      
      // Deduct gold cost
      this.gameState.deductGold(towerCost);
      console.log(`Deducted ${towerCost} gold for tower placement`);
      
      // Remove nearby static objects that might interfere
      this.removeStaticObjectsNearTower(position, 2); // 2 unit radius
    } else {
      console.error("Failed to create tower");
    }
  }
  
  /**
   * Remove static objects near a tower position
   * @param position The position where the tower will be placed
   * @param towerSize The placement radius of the tower
   */
  private removeStaticObjectsNearTower(position: THREE.Vector3, towerSize: number): void {
    if (!this.objectManager) {
      return;
    }
    
    console.log(`Removing static objects within ${towerSize} units of tower placement at ${position.x.toFixed(2)}, ${position.y.toFixed(2)}, ${position.z.toFixed(2)}`);
    
    // Get objects within tower placement radius
    const objectsInRadius = this.objectManager.getObjectsInRadius(position, towerSize);
    const objectsToRemove: GameObject[] = [];
    
    // Collect all static objects within the tower's placement area (except loafs and towers)
    for (const object of objectsInRadius) {
      // Skip if it's a loaf, a tower, or any other dynamic object
      if (object instanceof LODLoaf || object instanceof BlenderTower || object.getType() !== ObjectType.STATIC) {
        continue;
      }
      
      objectsToRemove.push(object);
    }
    
    if (objectsToRemove.length === 0) {
      console.log('No static objects found to remove');
      return;
    }
    
    console.log(`Found ${objectsToRemove.length} static objects to remove`);
    
    // Remove all collected objects using the object manager
    for (const object of objectsToRemove) {
      try {
        console.log(`Removing static object ${object.getName()} (ID: ${object.getId()})`);
        
        // Let the object manager handle all the cleanup
        this.objectManager.remove(object.getId());
      } catch (error) {
        console.error(`Failed to remove object ${object.getId()}:`, error);
      }
    }
  }
  
  private setWeaponMode(mode: WeaponMode): void {
    // Skip if trying to set the same mode
    if (mode === this.currentMode) return;
    
    console.log(`Switching weapon mode from ${WeaponMode[this.currentMode]} to ${WeaponMode[mode]}`);
    
    // Update the mode
    this.currentMode = mode;
    
    // Reset button styles
    this.towerButton.style.backgroundColor = 'rgba(80, 80, 80, 0.8)';
    this.gunButton.style.backgroundColor = 'rgba(80, 80, 80, 0.8)';
    this.autoGunButton.style.backgroundColor = 'rgba(80, 80, 80, 0.8)';
    
    // Update tower placement indicator visibility
    if (this.towerPlacementIndicator) {
      this.towerPlacementIndicator.visible = mode === WeaponMode.TOWER_PLACEMENT;
    }
    
    // Update button styles based on mode
    switch (mode) {
      case WeaponMode.TOWER_PLACEMENT:
        this.towerButton.style.backgroundColor = 'rgba(0, 100, 200, 0.8)';
        break;
      case WeaponMode.GUN:
        this.gunButton.style.backgroundColor = 'rgba(200, 50, 50, 0.8)';
        break;
      case WeaponMode.AUTOMATIC_GUN:
        this.autoGunButton.style.backgroundColor = 'rgba(0, 200, 100, 0.8)';
        break;
    }
    
    // Stop any automatic firing when switching modes
    this.isAutoFiring = false;
    
    // Update mobile action buttons text
    if (this.isMobileControlsActive) {
      this.updateMobileActionButtons();
    }
  }
  
  /**
   * Update the mobile action buttons based on current weapon mode
   */
  private updateMobileActionButtons(): void {
    if (this.leftActionButton) {
      this.leftActionButton.style.display = 
        this.currentMode === WeaponMode.TOWER_PLACEMENT ? 'flex' : 'none';
    }
    
    if (this.rightActionButton) {
      if (this.currentMode === WeaponMode.GUN) {
        this.rightActionButton.textContent = 'Fire';
        this.rightActionButton.style.display = 'flex';
      } else {
        this.rightActionButton.style.display = 'none';
      }
    }
  }
  
  public getCurrentMode(): WeaponMode {
    return this.currentMode;
  }
  
  public showUI(): void {
    this.weaponUI.style.display = 'flex';
    this.crosshair.style.display = 'block';
    
    // Show mobile controls if active
    if (this.isMobileControlsActive && this.runButton) {
      this.runButton.style.display = 'flex';
    }
  }
  
  public hideUI(): void {
    this.weaponUI.style.display = 'none';
    this.crosshair.style.display = 'none';
    
    // Hide mobile controls
    if (this.runButton) {
      this.runButton.style.display = 'none';
    }
  }
  
  /**
   * Set the object manager - allows setting it after initialization if it wasn't available
   */
  public setObjectManager(objectManager: ObjectManager): void {
    this.objectManager = objectManager;
  }
  
  /**
   * Set the object factory - necessary for creating towers properly
   */
  public setObjectFactory(objectFactory: ObjectFactory): void {
    this.objectFactory = objectFactory;
  }
  
  /**
   * Fire a single shot from the gun
   */
  public fireGun(): void {
    const gunCost = this.gameState.getGunCost();
    
    // Check if player has enough gold
    if (!this.gameState.hasEnoughGold(gunCost)) {
      console.log(`Cannot fire gun: Not enough gold (need ${gunCost}, have ${this.gameState.getGold()})`);
      this.showNotEnoughGoldMessage();
      return;
    }
    
    console.log('Firing gun...');
    
    // Deduct gold for firing
    this.gameState.deductGold(gunCost);
    
    // Fire a projectile from the gun
    this.fireProjectileFromGun(ProjectileType.BASIC, 50, gunCost);
  }
  
  /**
   * Fire the automatic gun
   */
  private fireAutoGun(): void {
    // Check if player has enough gold
    if (!this.gameState.hasEnoughGold(this.autoGunCostPerShot)) {
      this.isAutoFiring = false; // Stop firing if out of gold
      this.showNotEnoughGoldMessage();
      return;
    }
    
    // Deduct gold for firing
    this.gameState.deductGold(this.autoGunCostPerShot);
    
    // Fire a rapid-fire projectile from the gun
    this.fireProjectileFromGun(ProjectileType.RAPID_FIRE, 100, this.autoGunCostPerShot, true);
  }
  
  /**
   * Fire a projectile from the gun position towards the crosshair
   * @param projectileType The type of projectile to fire
   * @param speed The speed of the projectile
   * @param cost The cost of firing
   * @param applySpread Whether to apply random spread to the projectile direction
   */
  private fireProjectileFromGun(
    projectileType: ProjectileType, 
    speed: number, 
    cost: number,
    applySpread: boolean = false
  ): void {
    if (!this.projectileSystem) {
      console.error('Cannot fire gun: ProjectileSystem not available');
      return;
    }
    
    const camera = this.camera.getThreeCamera();
    
    // Calculate the spawn position for the projectile (at the gun's barrel tip)
    let spawnPosition: THREE.Vector3;
    
    if (this.gunModel) {
      // Calculate barrel tip world position based on gun model
      spawnPosition = this.gunBarrelTip.clone();
      spawnPosition.applyQuaternion(camera.quaternion);
      spawnPosition.add(camera.position);
      
      // Apply the gun offset to the barrel tip position
      const gunOffset = this.gunOffset.clone();
      gunOffset.applyQuaternion(camera.quaternion);
      spawnPosition.add(gunOffset);
    } else {
      // Fallback: use camera position if gun model not available
      spawnPosition = camera.position.clone();
      spawnPosition.y -= 0.2; // Slightly below eye level
    }
    
    // Get the camera's forward direction
    const direction = new THREE.Vector3(0, 0, -1);
    direction.applyQuaternion(camera.quaternion);
    
    // Apply random spread for automatic weapons if requested
    if (applySpread) {
      const spread = this.autoGunSpread;
      direction.x += (Math.random() - 0.5) * spread;
      direction.y += (Math.random() - 0.5) * spread;
      direction.z += (Math.random() - 0.5) * spread;
      direction.normalize();
    }
    
    // Fire the projectile
    this.projectileSystem.fireProjectile(
      projectileType,
      spawnPosition,
      direction,
      speed,
      projectileType === ProjectileType.RAPID_FIRE ? 8 : 25 // Lower damage for rapid fire
    );
  }
  
  private showNotEnoughGoldMessage(): void {
    // Create a temporary message element
    const message = document.createElement('div');
    message.textContent = '💰 Not enough gold!';
    message.style.position = 'absolute';
    message.style.top = '50%';
    message.style.left = '50%';
    message.style.transform = 'translate(-50%, -50%)';
    message.style.backgroundColor = 'rgba(255, 0, 0, 0.6)';
    message.style.color = 'white';
    message.style.padding = '10px 15px';
    message.style.borderRadius = '5px';
    message.style.fontFamily = 'Arial, sans-serif';
    message.style.fontWeight = 'bold';
    message.style.pointerEvents = 'none';
    message.style.zIndex = '1000';
    
    // Add to document and remove after a short delay
    document.body.appendChild(message);
    setTimeout(() => {
      document.body.removeChild(message);
    }, 1500);
  }
  
  /**
   * Set the projectile system
   */
  public setProjectileSystem(projectileSystem: ProjectileSystem): void {
    this.projectileSystem = projectileSystem;
  }
  
  /**
   * Set up mobile controls with nipplejs
   */
  public setupMobileControls(): void {
    console.log('WeaponSystem: Setting up mobile controls with nipplejs');
    
    // Create containers for joysticks
    const leftJoystickContainer = document.createElement('div');
    leftJoystickContainer.id = 'left-joystick-container';
    leftJoystickContainer.style.position = 'absolute';
    leftJoystickContainer.style.left = '30px';
    leftJoystickContainer.style.bottom = '30px';
    leftJoystickContainer.style.width = '120px';
    leftJoystickContainer.style.height = '120px';
    document.body.appendChild(leftJoystickContainer);
    
    // Create Run button (above left joystick)
    this.runButton = document.createElement('div');
    this.runButton.id = 'run-button';
    this.runButton.style.position = 'absolute';
    this.runButton.style.left = '30px';
    this.runButton.style.bottom = '140px';
    this.runButton.style.width = '60px';
    this.runButton.style.height = '40px';
    this.runButton.style.borderRadius = '20px';
    this.runButton.style.backgroundColor = 'rgba(100, 100, 100, 0.7)';
    this.runButton.style.display = 'flex';
    this.runButton.style.alignItems = 'center';
    this.runButton.style.justifyContent = 'center';
    this.runButton.style.color = 'white';
    this.runButton.style.fontWeight = 'bold';
    this.runButton.style.fontSize = '14px';
    this.runButton.style.textTransform = 'uppercase';
    this.runButton.style.transition = 'background-color 0.2s, transform 0.1s';
    this.runButton.style.boxShadow = '0 2px 4px rgba(0,0,0,0.3)';
    this.runButton.textContent = 'WALK';
    document.body.appendChild(this.runButton);
    
    // Handle Run button touch events
    this.runButton.addEventListener('touchstart', (e) => {
      e.preventDefault();
      this.toggleRunMode();
    });
    
    // Create right joystick container
    const rightJoystickContainer = document.createElement('div');
    rightJoystickContainer.id = 'right-joystick-container';
    rightJoystickContainer.style.position = 'absolute';
    rightJoystickContainer.style.right = '30px';
    rightJoystickContainer.style.bottom = '30px';
    rightJoystickContainer.style.width = '120px';
    rightJoystickContainer.style.height = '120px';
    document.body.appendChild(rightJoystickContainer);
    
    // Create left action button (for tower placement)
    this.leftActionButton = document.createElement('div');
    this.leftActionButton.id = 'left-action-button';
    this.leftActionButton.style.position = 'absolute';
    this.leftActionButton.style.left = '110px';
    this.leftActionButton.style.bottom = '110px';
    this.leftActionButton.style.width = '60px';
    this.leftActionButton.style.height = '60px';
    this.leftActionButton.style.borderRadius = '50%';
    this.leftActionButton.style.backgroundColor = 'rgba(255, 0, 0, 0.6)';
    this.leftActionButton.style.display = 'flex';
    this.leftActionButton.style.alignItems = 'center';
    this.leftActionButton.style.justifyContent = 'center';
    this.leftActionButton.style.color = 'white';
    this.leftActionButton.style.fontWeight = 'bold';
    this.leftActionButton.style.fontSize = '14px';
    this.leftActionButton.textContent = 'Place';
    document.body.appendChild(this.leftActionButton);
    
    // Create right action button (for firing gun)
    this.rightActionButton = document.createElement('div');
    this.rightActionButton.id = 'right-action-button';
    this.rightActionButton.style.position = 'absolute';
    this.rightActionButton.style.right = '110px';
    this.rightActionButton.style.bottom = '110px';
    this.rightActionButton.style.width = '60px';
    this.rightActionButton.style.height = '60px';
    this.rightActionButton.style.borderRadius = '50%';
    this.rightActionButton.style.backgroundColor = 'rgba(0, 0, 255, 0.6)';
    this.rightActionButton.style.display = 'flex';
    this.rightActionButton.style.alignItems = 'center';
    this.rightActionButton.style.justifyContent = 'center';
    this.rightActionButton.style.color = 'white';
    this.rightActionButton.style.fontWeight = 'bold';
    this.rightActionButton.style.fontSize = '14px';
    this.rightActionButton.textContent = 'Fire';
    document.body.appendChild(this.rightActionButton);
    
    // Create left joystick (movement)
    this.leftJoystick = nipplejs.create({
      zone: leftJoystickContainer,
      mode: 'static',
      position: { left: '50%', top: '50%' },
      color: 'rgba(255, 255, 255, 0.5)',
      size: 100
    });
    
    // Create right joystick (look)
    this.rightJoystick = nipplejs.create({
      zone: rightJoystickContainer,
      mode: 'static',
      position: { left: '50%', top: '50%' },
      color: 'rgba(255, 255, 255, 0.5)',
      size: 100
    });
    
    // Set up left joystick for movement (WASD replacement)
    this.leftJoystick.on('move', (evt: any, data: any) => {
      const force = Math.min(data.force, 1.0); // Normalize force to 0-1
      const angle = data.angle.radian;
      
      // Convert polar coordinates to cartesian
      // For movement, we need to adjust the coordinate system:
      // Forward is negative Z (up on screen)
      // Right is positive X (right on screen)
      // We need to map joystick angle correctly to WASD inputs
      
      // In nipplejs, angle 0 is right, PI/2 is up, PI is left, 3PI/2 is down
      this.mobileMovementVector.x = Math.cos(angle) * force; // Left/right
      this.mobileMovementVector.y = Math.sin(angle) * force; // Forward/back
    });
    
    this.leftJoystick.on('end', () => {
      // Reset movement when joystick is released
      this.mobileMovementVector.set(0, 0);
      
      // Make sure to release all movement keys when joystick is released
      this.input.simulateKeyRelease('w');
      this.input.simulateKeyRelease('a');
      this.input.simulateKeyRelease('s');
      this.input.simulateKeyRelease('d');
      this.input.simulateKeyRelease('shift'); // Also release shift key
      
      console.log('Mobile movement joystick released - stopping movement');
    });
    
    // Set up right joystick for looking (mouse replacement)
    let activeLookDirectionLocked = false;
    let isHorizontalLookActive = false;
    
    this.rightJoystick.on('start', () => {
      // Reset direction lock when joystick starts moving
      activeLookDirectionLocked = false;
    });
    
    this.rightJoystick.on('move', (evt: any, data: any) => {
      const force = Math.min(data.force, 1.5); // Limit the maximum force for better control
      const angle = data.angle.radian;
      
      // If direction is not locked yet, determine which direction to use
      if (!activeLookDirectionLocked && force > 0.3) {
        // Only lock in a direction when there's sufficient force to be intentional
        isHorizontalLookActive = Math.abs(Math.cos(angle)) > Math.abs(Math.sin(angle));
        activeLookDirectionLocked = true;
      }
      
      // Reset both vectors first
      this.mobileLookVector.set(0, 0);
      
      if (activeLookDirectionLocked) {
        if (isHorizontalLookActive) {
          // ONLY allow horizontal movement (left/right looking)
          this.mobileLookVector.x = Math.cos(angle) * force;
        } else {
          // ONLY allow vertical movement (up/down looking)
          this.mobileLookVector.y = Math.sin(angle) * force;
        }
      }
    });
    
    this.rightJoystick.on('end', () => {
      // Stop looking when joystick is released
      this.mobileLookVector.set(0, 0);
      activeLookDirectionLocked = false;
    });
    
    // Set up action buttons
    if (this.leftActionButton) {
      this.leftActionButton.addEventListener('touchstart', (e) => {
        e.preventDefault();
        if (this.currentMode === WeaponMode.TOWER_PLACEMENT) {
          this.placeTower();
        }
      });
    }
    
    if (this.rightActionButton) {
      this.rightActionButton.addEventListener('touchstart', (e) => {
        e.preventDefault();
        if (this.currentMode === WeaponMode.GUN) {
          this.fireGun();
        }
      });
      
      this.rightActionButton.addEventListener('touchend', (e) => {
        e.preventDefault();
        // No-op, as auto gun mode is removed
      });
    }
    
    // Modify weapon buttons UI positioning for mobile
    this.updateWeaponUIForMobile();
    
    // Flag that mobile controls are active
    this.isMobileControlsActive = true;
  }
  
  /**
   * Update weapon UI positioning for mobile
   */
  private updateWeaponUIForMobile(): void {
    // Simplify button text for mobile
    this.towerButton.textContent = "Tower";
    this.gunButton.textContent = "Gun";
    
    // Make buttons smaller and more touch-friendly
    [this.towerButton, this.gunButton].forEach(button => {
      button.style.fontSize = '12px';
      button.style.padding = '5px 8px';
      button.style.margin = '3px 0';
    });
  }
  
  /**
   * Toggle between walk and run modes for mobile controls
   */
  private toggleRunMode(): void {
    this.isRunActive = !this.isRunActive;
    
    if (this.runButton) {
      if (this.isRunActive) {
        this.runButton.textContent = 'RUN';
        this.runButton.style.backgroundColor = 'rgba(0, 200, 100, 0.7)';
        this.runButton.style.transform = 'scale(1.05)';
      } else {
        this.runButton.textContent = 'WALK';
        this.runButton.style.backgroundColor = 'rgba(100, 100, 100, 0.7)';
        this.runButton.style.transform = 'scale(1)';
      }
    }
  }
} 